export default `
<div>
  <p><strong>Three Days, Four Signals of Change</strong></p>

  <p>
    GDPS 2026 just wrapped up. Over three days, we walked the floor as observers — and what we saw offered one of the most grounded, unfiltered looks at where AI deployment actually stands today.
  </p>

  <p>
    In one sentence: <strong>AI has moved from "can it be done?" to "how do we turn it into a business?"</strong>
  </p>

  <p>
    FutureX founding partner Cynthia Zhang walked through the exhibition booths and observed:
  </p>

  <p>
    <em>"This expo was completely different from GTC. GTC had more ToB supply chain vendors; this time there were far more AI hardware products, especially in AI + emotion and AI + education. And everyone was paying close attention to OpenClaw and OPC."</em>
    — Cynthia Zhang, Founding Partner, FutureX Capital
  </p>

  <p>Behind this observation are several key signals:</p>

  <p><strong>1. AI Application Maturity Is Accelerating</strong></p>

  <p>
    Compared to last year, the entrepreneurs at the booths were no longer just saying "we're building an AI product." They could clearly articulate who their users are, how the business model works, and their monthly revenue figures. The leap from "proof of concept" to "commercial deployment" was undeniable.
  </p>

  <p><strong>2. Super Entrepreneurs and One-Person Companies Are No Longer Just a Concept</strong></p>

  <p>
    The conference featured a dedicated Super Entrepreneur Innovation Competition. We also met several founders who were essentially "one-person companies." AI is redefining the threshold for entrepreneurship and organizational structures — a trend closely aligned with FutureX's long-standing focus.
  </p>

  <p><strong>3. The Agent Ecosystem Is Rapidly Taking Shape</strong></p>

  <p>
    The conference hackathon focused on agents' ability to execute complex tasks end-to-end. The early form of the OpenClaw ecosystem and Skills marketplace was already visible. Talking with teams like Lemon AI on the floor made one thing clear: developers have an extremely strong demand for open-source, low-cost agent solutions — and the market is only beginning to respond.
  </p>

  <p><strong>4. AI Is Moving from "Productivity Tool" to "Human-Centric Scenarios"</strong></p>

  <p>
    Cynthia Zhang's observation was particularly interesting — AI + emotion, AI + education. These aren't the traditional VC narratives about "efficiency gains," but rather something closer to fundamental human needs. Amoo from QingXinYiChuang enables emotional interaction, and Viaim's AI earbuds are redefining the experience of "listening." At their core, they're all answering the same question: how can AI make people's lives different, not just make work faster.
  </p>

  <p><strong>Five Companies That Stood Out at GDPS</strong></p>

  <p>
    Among hundreds of exhibitors, five companies left a particularly strong impression — not just because of what their products can do, but because of how real the demand around them felt on the floor.
  </p>

  <p><strong>QingXinYiChuang: AMOO Embodied Character Robot</strong></p>

  <p>
    The moment it walked over, everyone wanted to touch it. QingXinYiChuang brought not a cold industrial robot, but the world's first embodied character intelligent robot Amoo — a dragon-character design under 80cm tall, with a soft, skin-friendly body and expressive eyes that autonomously match emotions to the conversation. Call it "silly" and it immediately glares in protest; compliment it as cute, and it puts its hands together to "send love." Every interaction response takes less than 30 milliseconds, with eyes, speech, and body movements perfectly synchronized — like a little life with a real personality. The booth was constantly surrounded by crowds.
  </p>

  <p><strong>Viaim: AI Earbuds</strong></p>

  <p>
    "Listening" has been redefined. Viaim showcased AI earbuds with built-in large language models. This isn't just noise cancellation or translation — it uses AI to fundamentally redefine the listening experience with real-time transcription, intelligent summarization, and multilingual simultaneous interpretation. Many visitors' first reaction after trying them: "I need these for meetings."
  </p>

  <p><strong>Lemon AI: Open-Source AI Agent</strong></p>

  <p>
    The inquiries at the booth never stopped. Lemon AI's pitch was direct: task costs at one-tenth of competitors, open-source architecture you can modify yourself. Developers kept coming, each with more technical questions than the last. Several left their contact information on the spot, saying they'd seriously try it when they got back. In a sea of "AI-powered" claims, this was one of the few booths where the product clearly spoke for itself.
  </p>

  <p><strong>XianShun Technology: Near-Eye Display</strong></p>

  <p>
    The "hidden champion" behind AR glasses was finally seen. XianShun Technology showcased one of the world's smallest near-eye display systems at the exhibition. For most visitors, this was their first glimpse at what the core technology supplier behind AR glasses actually looks like. Foundational, easy to overlook — and exactly the kind of company that tends to matter most in the long run.
  </p>

  <p><strong>SmartCore Digital: AI Digital Workers for Manufacturing</strong></p>

  <p>
    They brought agents into the factory. At the conference, "Agent" was one of the most frequently mentioned words, but most discussions remained in the digital world. SmartCore Digital brought digital workers into real manufacturing scenarios, helping factories achieve tangible efficiency gains. A quiet but meaningful signal of AI moving from "online" to "offline."
  </p>

  <p>
    From humanoid robots to AI earbuds, from open-source agents to near-eye displays, from software to factory floors — five companies, five very different visions of where AI is heading next.
  </p>

  <p><strong>Our View: AI's Window of Opportunity Has Just Begun</strong></p>

  <p>
    Over three days, the most frequently asked question at our booth was: "Are you still actively investing?"
  </p>

  <p>The answer: of course.</p>

  <p>
    <em>"Regardless of which technology wave, the greatest commercial value ultimately comes from applications, not technology alone. This was true in the internet era, true in the mobile internet era, and equally true in the AI era."</em>
    — Cynthia Zhang, Founding Partner, FutureX Capital
  </p>

  <p>
    Over the past two years, FutureX has engaged with numerous AI companies domestically and internationally, witnessing firsthand the transformation of AI applications from "wrapper" skepticism to genuine commercial value creation. The real-world cases we saw at this conference only deepened that conviction.
  </p>

  <p>
    <em>"For VCs who truly understand AI, this is an excellent window of opportunity. FutureX will continue to go deep in the AI application space, seeking non-consensus opportunities within the broader consensus."</em>
    — Cynthia Zhang, Founding Partner, FutureX Capital
  </p>

  <p><strong>This Is Not the End, but the Beginning</strong></p>

  <p>
    Three days of the conference have ended, but for FutureX, this is just a beginning. We believe AI is reshaping every industry, every way of working, and everyone's daily life. In this transformation, what matters most isn't pure technology itself, but the entrepreneurs who can turn technology into products and products into value.
  </p>

  <p>
    Thank you to everyone who stopped by booths A03-A04 to chat. Over three days, we discussed countless fascinating projects and met so many remarkable people.
  </p>
</div>
`;
